import React from "react";

import { faExclamationTriangle } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { AppContext } from "~/common";

import type { Participant } from "../hooks/useParticipantsOptions";

import type { CustomRecurringRuleSerializerInput, repeat } from "~/__generated__/useSaveEventMutationMutation.graphql";

import styles from "./MeetingGuidelines.module.css";

interface FiniteRecurringMeetingsProps {
	repeatState: repeat;
	customRrule: CustomRecurringRuleSerializerInput | null;
	attendees: Partial<Participant>[];
	setRendered: (rendered: boolean) => void;
}

export default function FiniteRecurringMeetings({
	repeatState,
	customRrule,
	attendees,
	setRendered,
}: FiniteRecurringMeetingsProps) {
	const { user } = React.useContext(AppContext);

	const shouldRenderPrompt = React.useMemo(() => {
		let requiredAttendees = attendees.filter(attendee => !attendee?.isOptional);
		const userInAttendees = requiredAttendees.find(attendee => attendee.id === user?.id);
		if (!userInAttendees) {
			// Add empty entry to the array to account for the organizer
			requiredAttendees = [{}, ...requiredAttendees];
		}

		const isRecurring = repeatState !== "NEVER";
		const hasEnd = customRrule?.untilCount || customRrule?.untilDate;

		return isRecurring && !hasEnd && requiredAttendees.length > 2;
	}, [attendees, repeatState, customRrule?.untilCount, customRrule?.untilDate, user?.id]);

	React.useEffect(() => {
		setRendered(shouldRenderPrompt);

		return () => {
			setRendered(false);
		};
	}, [setRendered, shouldRenderPrompt]);

	if (!shouldRenderPrompt) return null;

	return (
		<div className={styles.meetingPrompt}>
			<div className={styles.meetingPromptIcon}>
				<FontAwesomeIcon fixedWidth icon={faExclamationTriangle} />
			</div>
			<div>
				<div className={styles.headlineText}>Set an end date for this recurring meeting</div>
				<div className={styles.supportingText}>Your recurring meetings need to have an end date.</div>
			</div>
		</div>
	);
}
